import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Mail, MapPin, ArrowRight } from 'lucide-react';
import IDCoderBrandLockup from '../brand/IDCoderBrandLockup';
import { BRAND } from '../../config/brand';
import { PROGRAM_SERIES } from '../../content/programSeries';
import { usePublicNav } from '../../hooks/usePublicNav';
import { isNavMenuVisible, navMenuLabel } from '../../utils/navMenus';

const Footer: React.FC = () => {
  const { navItems, hasPublishedEvents, hasPublishedGigs } = usePublicNav();

  const navFlags = { hasPublishedEvents, hasPublishedGigs };
  const menuVisible = (key: string) => isNavMenuVisible(key, navItems, navFlags);
  const menuLabel = (key: string, fallback: string) => navMenuLabel(key, navItems, fallback);

  return (
    <footer className="site-footer relative overflow-hidden text-white">
      <div className="pointer-events-none absolute inset-0 opacity-[0.18]" aria-hidden>
        <div className="site-footer__orb--a absolute -top-24 right-10 h-64 w-64 rounded-full blur-3xl" />
        <div className="site-footer__orb--b absolute bottom-0 left-1/4 h-48 w-48 rounded-full blur-3xl" />
      </div>
      <div className="relative max-w-7xl mx-auto">
        <div className="px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <div className="mb-4">
                <IDCoderBrandLockup variant="dark" />
              </div>
              <p className="text-gray-400 text-sm mb-6 max-w-md">
                {BRAND.description}
              </p>
              <div className="flex space-x-4">
                <a href="#" className="text-gray-400 hover:text-primary-400 transition-colors">
                  <Instagram className="h-5 w-5" />
                  <span className="sr-only">Instagram</span>
                </a>
                <a href="#" className="text-gray-400 hover:text-primary-400 transition-colors">
                  <Linkedin className="h-5 w-5" />
                  <span className="sr-only">LinkedIn</span>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Explore</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Home
                  </Link>
                </li>
                {menuVisible('courses') && (
                  <li>
                    <Link to="/courses" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('courses', 'Courses')}
                    </Link>
                  </li>
                )}
                {menuVisible('labs') && (
                  <li>
                    <Link to="/labs" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('labs', 'Labs')}
                    </Link>
                  </li>
                )}
                {menuVisible('projects') && (
                  <li>
                    <Link to="/projects" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('projects', 'Projects')}
                    </Link>
                  </li>
                )}
                {menuVisible('programs') && (
                  <li>
                    <Link to={PROGRAM_SERIES.programsListPath} className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('programs', PROGRAM_SERIES.navPrograms)}
                    </Link>
                  </li>
                )}
                {menuVisible('interviews') && (
                  <li>
                    <Link to="/interviews" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('interviews', PROGRAM_SERIES.navInterview)}
                    </Link>
                  </li>
                )}
                {menuVisible('events') && (
                  <li>
                    <Link to="/events" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('events', 'Events')}
                    </Link>
                  </li>
                )}
                {menuVisible('gigs') && (
                  <li>
                    <Link to="/gigs" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('gigs', 'Gigs')}
                    </Link>
                  </li>
                )}
                {menuVisible('contact') && (
                  <li>
                    <Link to="/contact" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      {menuLabel('contact', 'Contact')}
                    </Link>
                  </li>
                )}
                <li>
                  <Link to="/terms" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Terms &amp; Conditions
                  </Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Privacy Policy
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Get Involved</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/mentor-registration" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Apply as a Mentor
                  </Link>
                </li>
                <li>
                  <Link to="/interviewer-registration" className="text-gray-300 hover:text-primary-400 transition-colors flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Apply as an Interviewer
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">Contact</h3>
              <ul className="space-y-4">
                <li className="flex items-start">
                  <Mail className="h-5 w-5 text-primary-400 mr-3 mt-0.5" />
                  <div>
                    <span className="text-gray-300">administrator@idcoder.in</span>
                    <p className="text-sm text-gray-400 mt-1">We'll respond within 24 hours</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <MapPin className="h-5 w-5 text-primary-400 mr-3 mt-0.5" />
                  <div>
                    <span className="text-gray-300">Hyderabad, Telangana, India</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-800">
          <div className="px-4 py-6 sm:px-6 lg:px-8">
            <p className="text-gray-400 text-sm">
              &copy; {new Date().getFullYear()} {BRAND.name}. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
